import { Download, Package, Plus, RefreshCw, Search } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, getApiErrorMessage } from '../../api/client';
import type { CargoCompanyRow, CargoInvoiceRow, CargoOverview, Pagination } from '../../api/types';
import { CargoCreateCompanyModal } from '../../components/cargo/CargoCreateCompanyModal';
import { CargoCompanyDetail, CargoInvoiceList } from '../../components/cargo/CargoCompanyDetail';
import { CargoInvoiceDetailModal } from '../../components/cargo/CargoInvoiceDetailModal';
import { CargoStatsGrid } from '../../components/cargo/CargoStatsGrid';
import { useAuth } from '../../context/AuthContext';
import {
  companyStatusBadge,
  companyStatusLabel,
  filterCompanies,
  formatDateTime,
} from '../../utils/cargoStatus';
import { canManageCargo } from '../../utils/roles';

export function CargoSection() {
  const { role } = useAuth();
  const canManage = canManageCargo(role);

  const [overview, setOverview] = useState<CargoOverview | null>(null);
  const [companies, setCompanies] = useState<CargoCompanyRow[]>([]);
  const [companyPagination, setCompanyPagination] = useState<Pagination | null>(null);
  const [invoices, setInvoices] = useState<CargoInvoiceRow[]>([]);
  const [invoicePagination, setInvoicePagination] = useState<Pagination | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyQuery, setCompanyQuery] = useState('');
  const [companyStatus, setCompanyStatus] = useState('');
  const [companyPage, setCompanyPage] = useState(1);
  const [invoiceStatus, setInvoiceStatus] = useState('');
  const [invoiceSplit, setInvoiceSplit] = useState('');
  const [invoiceQuery, setInvoiceQuery] = useState('');
  const [invoicePage, setInvoicePage] = useState(1);
  const [copied, setCopied] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, co, inv] = await Promise.all([
        api.cargoOverview(),
        api.listCargoCompanies({
          page: companyPage,
          limit: 50,
          status: companyStatus || undefined,
        }),
        api.listCargoInvoices({
          page: invoicePage,
          limit: 50,
          status: invoiceStatus || undefined,
          split_status: invoiceSplit || undefined,
          q: invoiceQuery.trim() || undefined,
        }),
      ]);
      setOverview(ov.stats);
      setCompanies(co.companies);
      setCompanyPagination(co.pagination);
      setInvoices(inv.invoices);
      setInvoicePagination(inv.pagination);
      setError(null);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [companyPage, companyStatus, invoicePage, invoiceStatus, invoiceSplit, invoiceQuery]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredCompanies = filterCompanies(companies, companyQuery);

  const copyText = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* ignore */
    }
  };

  const exportCsv = async () => {
    try {
      const blob = await api.exportCargoInvoicesCsv({
        status: invoiceStatus || undefined,
        split_status: invoiceSplit || undefined,
        q: invoiceQuery.trim() || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cargo-invoices-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  if (selectedCompanyId) {
    return (
      <CargoCompanyDetail
        companyId={selectedCompanyId}
        onBack={() => {
          setSelectedCompanyId(null);
          void load();
        }}
      />
    );
  }

  return (
    <>
      <div className="section-header cargo-section-header">
        <div>
          <h1 className="console-title">Cargo partners</h1>
          <p className="console-sub">B2B invoice partners · settlement &amp; operations</p>
        </div>
        <div className="cargo-header-actions">
          {canManage ? (
            <button type="button" className="btn-primary btn-inline" onClick={() => setCreateOpen(true)}>
              <Plus size={16} aria-hidden />
              New partner
            </button>
          ) : null}
          <button type="button" className="btn-outline btn-inline" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'cargo-spin' : undefined} aria-hidden />
            Refresh
          </button>
        </div>
      </div>

      {error ? <p className="form-error section-error">{error}</p> : null}

      <CargoStatsGrid stats={overview} loading={loading} />

      <div className="cargo-panel card">
        <div className="cargo-panel-head">
          <div className="cargo-panel-title-wrap">
            <Package size={16} color="var(--onyx-gold)" aria-hidden />
            <h2 className="cargo-panel-title">Partner directory</h2>
          </div>
          <div className="cargo-filters-row">
            <select
              className="cargo-filter-select"
              value={companyStatus}
              onChange={(e) => {
                setCompanyStatus(e.target.value);
                setCompanyPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="pending">Pending approval</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
            <div className="cargo-search-wrap">
              <Search size={14} className="cargo-search-icon" aria-hidden />
              <input
                type="search"
                className="cargo-search-input"
                placeholder="Search by name or code…"
                value={companyQuery}
                onChange={(e) => setCompanyQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="table-wrap cargo-table cargo-table-flush">
          <div className="table-head data-table-head cargo-companies-head cargo-companies-head-ext">
            <div className="th">Partner</div>
            <div className="th">Code</div>
            <div className="th">Country</div>
            <div className="th">Website</div>
            <div className="th">Status</div>
            <div className="th">Wallet</div>
            <div className="th">Team</div>
            <div className="th">Invoices</div>
            <div className="th">Volume</div>
            <div className="th">Registered</div>
          </div>
          {loading ? (
            <div className="cargo-empty-row">Loading partners…</div>
          ) : filteredCompanies.length === 0 ? (
            <div className="cargo-empty-state cargo-empty-state-compact">
              <p>{companies.length === 0 ? 'No cargo partners yet' : 'No matches'}</p>
            </div>
          ) : (
            filteredCompanies.map((c) => (
              <button
                key={c.id}
                type="button"
                className="table-row data-table-row cargo-companies-row cargo-companies-row-ext cargo-companies-row-btn"
                onClick={() => setSelectedCompanyId(c.id)}
              >
                <div className="cell-primary cargo-partner-name">{c.name}</div>
                <div>
                  <code className="cargo-hash">{c.company_code}</code>
                </div>
                <div className="cell-muted">{c.registration_country ?? '—'}</div>
                <div className="cell-muted cargo-website-cell">
                  {c.website ? c.website.replace(/^https?:\/\//, '') : '—'}
                </div>
                <div>
                  <span className={companyStatusBadge(c.status)}>{companyStatusLabel(c.status)}</span>
                </div>
                <div className={c.tron_payout_wallet ? 'cell-muted' : 'cargo-wallet-missing'}>
                  {c.tron_payout_wallet ? '✓' : '—'}
                </div>
                <div className="cargo-metric">{c.employee_count}</div>
                <div className="cargo-metric">{c.invoice_count}</div>
                <div className="cargo-metric">{c.volume_usdt ? Number.parseFloat(c.volume_usdt).toFixed(0) : '0'}</div>
                <div className="cell-muted">{formatDateTime(c.created_at)}</div>
              </button>
            ))
          )}
        </div>
        {companyPagination && companyPagination.pages > 1 ? (
          <div className="cargo-pagination">
            <button
              type="button"
              className="btn-outline btn-sm"
              disabled={companyPage <= 1}
              onClick={() => setCompanyPage((p) => p - 1)}
            >
              Previous
            </button>
            <span className="cell-muted">
              Page {companyPagination.page} / {companyPagination.pages}
            </span>
            <button
              type="button"
              className="btn-outline btn-sm"
              disabled={companyPage >= companyPagination.pages}
              onClick={() => setCompanyPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>

      <div className="cargo-panel card" style={{ marginTop: 20 }}>
        <div className="cargo-panel-head">
          <h2 className="cargo-panel-title">Invoices</h2>
          <div className="cargo-filters-row">
            <select
              className="cargo-filter-select"
              value={invoiceStatus}
              onChange={(e) => {
                setInvoiceStatus(e.target.value);
                setInvoicePage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="expired">Expired</option>
              <option value="aml_failed">AML failed</option>
            </select>
            <select
              className="cargo-filter-select"
              value={invoiceSplit}
              onChange={(e) => {
                setInvoiceSplit(e.target.value);
                setInvoicePage(1);
              }}
            >
              <option value="">All splits</option>
              <option value="pending">Split pending</option>
              <option value="completed">Split done</option>
              <option value="failed">Split failed</option>
            </select>
            <div className="cargo-search-wrap">
              <Search size={14} className="cargo-search-icon" aria-hidden />
              <input
                type="search"
                className="cargo-search-input"
                placeholder="Search hash, partner, order…"
                value={invoiceQuery}
                onChange={(e) => {
                  setInvoiceQuery(e.target.value);
                  setInvoicePage(1);
                }}
              />
            </div>
            <button type="button" className="btn-outline btn-sm btn-inline" onClick={() => void exportCsv()}>
              <Download size={14} aria-hidden />
              CSV
            </button>
          </div>
        </div>
        {loading ? (
          <div className="cargo-empty-row">Loading invoices…</div>
        ) : (
          <CargoInvoiceList
            rows={invoices}
            copied={copied}
            onCopy={copyText}
            onOpen={setSelectedInvoice}
            showCompany
          />
        )}
        {invoicePagination && invoicePagination.pages > 1 ? (
          <div className="cargo-pagination">
            <button
              type="button"
              className="btn-outline btn-sm"
              disabled={invoicePage <= 1}
              onClick={() => setInvoicePage((p) => p - 1)}
            >
              Previous
            </button>
            <span className="cell-muted">
              Page {invoicePagination.page} / {invoicePagination.pages} ({invoicePagination.total} total)
            </span>
            <button
              type="button"
              className="btn-outline btn-sm"
              disabled={invoicePage >= invoicePagination.pages}
              onClick={() => setInvoicePage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>

      <CargoCreateCompanyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={async (body) => {
          await api.createCargoCompany(body);
          await load();
        }}
      />

      <CargoInvoiceDetailModal
        hashId={selectedInvoice}
        open={Boolean(selectedInvoice)}
        onClose={() => setSelectedInvoice(null)}
        canManage={canManage}
        onUpdated={() => void load()}
      />
    </>
  );
}
